/**
 * 编辑器用的重复规则（与 iOS RepeatRule 一致），只生成 docs/API.md 第 4 节支持的 RRULE 子集。
 * 展开逻辑在服务端；这里只做"读回编辑器"和"显示文字"。
 */
import { WEEKDAY_NAMES } from "../../core";

export type RepeatKind = "none" | "daily" | "weekly" | "biweekly" | "monthly";

export const REPEAT_KINDS: readonly (readonly [RepeatKind, string])[] = [
  ["none", "不重复"],
  ["daily", "每天"],
  ["weekly", "每周"],
  ["biweekly", "隔周"],
  ["monthly", "每月"],
];

export interface RepeatRule {
  kind: RepeatKind;
  /** 1=周一 … 7=周日（升序、不重复） */
  days: number[];
}

const CODES = ["", "MO", "TU", "WE", "TH", "FR", "SA", "SU"];

export const NO_REPEAT: RepeatRule = { kind: "none", days: [] };

function normDays(days: Iterable<number>): number[] {
  return [...new Set(days)].filter((d) => d >= 1 && d <= 7).sort((a, b) => a - b);
}

/** 读回编辑器；startWeekday 用于每周规则没写 BYDAY 时 */
export function parseRepeat(rrule: string | null | undefined, startWeekday: number): RepeatRule {
  if (!rrule) return NO_REPEAT;
  const parts: Record<string, string> = {};
  for (const p of rrule.split(";")) {
    const i = p.indexOf("=");
    if (i > 0) parts[p.slice(0, i).trim().toUpperCase()] = p.slice(i + 1).trim();
  }
  const interval = Number.parseInt(parts.INTERVAL ?? "1", 10) || 1;
  switch (parts.FREQ?.toUpperCase()) {
    case "DAILY":
      return { kind: "daily", days: [] };
    case "WEEKLY": {
      let days = normDays(
        (parts.BYDAY ?? "")
          .split(",")
          .map((c) => CODES.indexOf(c.trim().toUpperCase()))
          .filter((i) => i > 0),
      );
      if (days.length === 0) days = [startWeekday];
      return { kind: interval >= 2 ? "biweekly" : "weekly", days };
    }
    case "MONTHLY":
      return { kind: "monthly", days: [] };
    default:
      return NO_REPEAT;
  }
}

function dayOfMonth(startDate: string): number {
  return Number.parseInt(startDate.slice(-2), 10) || 1;
}

/** 生成 RRULE；不重复返回 null。startDate 用于每月重复时取日期 */
export function buildRRule(r: RepeatRule, startDate: string): string | null {
  switch (r.kind) {
    case "none":
      return null;
    case "daily":
      return "FREQ=DAILY";
    case "weekly":
    case "biweekly": {
      const byday = normDays(r.days)
        .map((d) => CODES[d])
        .join(",");
      let s = "FREQ=WEEKLY";
      if (r.kind === "biweekly") s += ";INTERVAL=2";
      if (byday) s += `;BYDAY=${byday}`;
      return s;
    }
    case "monthly":
      return `FREQ=MONTHLY;BYMONTHDAY=${dayOfMonth(startDate)}`;
  }
}

/** "单次" / "每天" / "周一至周五" / "每周末" / "每周一、四" / "隔周六" / "每月 5 日" */
export function describeRepeat(r: RepeatRule, startDate: string): string {
  switch (r.kind) {
    case "none":
      return "单次";
    case "daily":
      return "每天";
    case "monthly":
      return `每月 ${dayOfMonth(startDate)} 日`;
    case "weekly":
    case "biweekly": {
      const sorted = normDays(r.days);
      const key = sorted.join(",");
      if (r.kind === "weekly" && key === "1,2,3,4,5") return "周一至周五";
      if (r.kind === "weekly" && key === "6,7") return "每周末";
      if (sorted.length === 7) return r.kind === "weekly" ? "每天" : "隔周每天";
      return (r.kind === "weekly" ? "每周" : "隔周") + sorted.map((d) => WEEKDAY_NAMES[d]).join("、");
    }
  }
}

/** 切换重复类型：改成每周 / 隔周且还没选星期时，默认选开始日期那天 */
export function withKind(r: RepeatRule, kind: RepeatKind, startWeekday: number): RepeatRule {
  const weekly = kind === "weekly" || kind === "biweekly";
  return { kind, days: weekly && r.days.length === 0 ? [startWeekday] : r.days };
}

/** 点一下星期：选中的去掉（至少留一天），没选的加上 */
export function toggleDay(r: RepeatRule, day: number): RepeatRule {
  if (r.days.includes(day)) return r.days.length > 1 ? { ...r, days: r.days.filter((d) => d !== day) } : r;
  return { ...r, days: normDays([...r.days, day]) };
}
